import { useEffect, useState } from "react";
import { endpoints, isTerminal, jobEventsUrl } from "../../api.js";
import { JOB_POLL_MS } from "../../config.js";

/**
 * Live job state. Uses SSE (`event: job` / `event: end`) when possible and
 * falls back to polling GET /jobs/:id when EventSource is unavailable, the
 * stream is refused (EventSource cannot send X-Tenant-Id or a bearer token),
 * or we're in OIDC mode.
 */
export function useJobStream(jobId, { preferSse = true } = {}) {
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [transport, setTransport] = useState(null); // "sse" | "poll" | null

  useEffect(() => {
    let alive = true;
    let es = null;
    let timer = null;
    setJob(null);
    setError(null);
    setTransport(null);

    const poll = async () => {
      try {
        const { job: j } = await endpoints.job(jobId);
        if (!alive) return;
        setJob(j);
        setError(null);
        if (isTerminal(j.status)) return;
      } catch (e) {
        if (!alive) return;
        setError(e);
        if ([401, 403, 404].includes(e.status)) return;
      }
      timer = setTimeout(poll, JOB_POLL_MS);
    };

    const startPolling = () => {
      setTransport("poll");
      timer = setTimeout(poll, JOB_POLL_MS);
    };

    const startStream = () => {
      if (!preferSse || typeof EventSource === "undefined") return startPolling();
      setTransport("sse");
      es = new EventSource(jobEventsUrl(jobId), { withCredentials: true });
      es.addEventListener("job", (ev) => {
        try {
          const j = JSON.parse(ev.data);
          if (alive) setJob(j);
        } catch {
          /* ignore malformed frame */
        }
      });
      es.addEventListener("end", () => {
        es?.close();
        es = null;
        endpoints
          .job(jobId)
          .then(({ job: j }) => alive && setJob(j))
          .catch(() => {});
      });
      es.onerror = () => {
        // CONNECTING => the browser retries by itself (safe per contract).
        // CLOSED => the server refused the stream; switch to polling.
        if (es && es.readyState === EventSource.CLOSED) {
          es = null;
          if (alive) startPolling();
        }
      };
    };

    endpoints.job(jobId).then(
      ({ job: j }) => {
        if (!alive) return;
        setJob(j);
        if (!isTerminal(j.status)) startStream();
      },
      (e) => alive && setError(e),
    );

    return () => {
      alive = false;
      es?.close();
      clearTimeout(timer);
    };
  }, [jobId, preferSse]);

  return { job, setJob, error, transport };
}
